import React, { useEffect, useRef } from "react";
import { Routes, Route, Navigate, Outlet, useSearchParams, useLocation } from "react-router-dom";

import UserMenu from "../components/UserMenu";
import Home from "../pages/User/Home";
import ActivityFeed from "../pages/User/ActivityFeed";
import Flagged from "../pages/User/Flagged/Flagged";
import Settings from "../pages/User/Settings";
import Notifications from "../pages/User/Notifications";
import CompanyDetails from "../pages/ViewCompany";
import NotificationDetails from "../pages/NotificationDetails";
import Suggestion from "../pages/Suggestion";
import UserLogin from "../pages/Login/User";
import { TermsAndConditions } from "../pages/User/TermsAndConditions";
import Unauthorized from "../pages/Unauthorized";
import { authSelector } from "../redux/modules/auth/userSelector";
import { useDispatch, useSelector } from "react-redux";
import PasswordReset from "../pages/PasswordReset";
import Requests from "../pages/User/Requests";
import { logout, whoAmI } from "../redux/modules/auth/actions";
import Spinner from "../components/Spinner";
import { reset } from "../redux/modules/users/slice";
import NotFound from "../pages/NotFound";
import { setThirdPartyUser } from "../redux/modules/thirdPartyUser/slice";
import AIResponse from "../pages/User/Home/aiResponse";
import Analytics from "../pages/User/Analytics";
import AlertMenu from "../pages/User/AlertMenu/AlertMenu";
import DragDrop from "../pages/User/DragDrop/DragDrop";
import UserTagsManager from "../pages/User/UserTags/UserTags"
import Landing from "../pages/Landing/Landing";

export default function UserRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<UserLogin />} />
      <Route path="airesponse" element={<AIResponse />} />
      <Route path="/thirdparty/auth" element={<ProxyLogin />} />
      <Route path="/" element={<PrivateRoute />}>
      <Route element={<UserMenu />}>
  <Route index element={<Navigate to="home" />} />

  <Route path="home" element={<Home />} />
  <Route path="user-tags" element={<UserTagsManager />} />
    <Route path="user-tags/company" element={<AlertMenu />} />

  <Route path="home/view/:id" element={<CompanyDetails />} />
  <Route path="home/view/:id/suggestion" element={<Suggestion />} />
  <Route path="home/view/:id/suggestion/:type" element={<Suggestion />} />

  <Route path="analytics" element={<Analytics />} />
  <Route path="analytics/view/:id" element={<CompanyDetails />} />

  <Route path="activity" element={<ActivityFeed />} />
  <Route path="requests" element={<Requests />} />
  <Route path="alerts" element={<AlertMenu />} />  
  <Route path="drag-and-drop" element={<DragDrop />} />
  <Route path="flagged" element={<Flagged />} />
  <Route path="flagged/view/:id" element={<CompanyDetails />} />
  <Route path="flagged/view/:id/suggestion" element={<Suggestion />} />
  <Route path="flagged/view/:id/suggestion/:type" element={<Suggestion />} />

  <Route path="settings" element={<Settings />} />
  <Route path="notifications" element={<Notifications />} />
  <Route path="notifications/view/:id" element={<NotificationDetails />} />
</Route>

        <Route path="terms" element={<TermsAndConditions />} />
      </Route>
      {/* <Route path="password-reset" element={<PasswordReset />} /> */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// UserRoutes.tsx — PrivateRoute, tightened spinner guard
function PrivateRoute() {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const { user, status } = useSelector(authSelector);

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    // Only call whoAmI if we have a token but no user, and we're not already in-flight or post-login
    if (!user && token && status !== "success" && status !== "loading") {
      dispatch(whoAmI());
    }
  }, [dispatch, user, status]);

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const showMarketing = !user && !token && location.pathname === "/";

  // Show spinner ONLY while whoAmI is genuinely in-flight, not after a fresh login
  if (status === "loading" && token && !user) return <Spinner />;
  
  // "idle" + token means useEffect hasn't fired yet (next tick) — show spinner briefly
  if (status === "idle" && token && !user) return <Spinner />;

  // Guests hitting the site root see the marketing landing page (URL stays "/").
  if (showMarketing) return <Landing />;

  if (!user) {
    if (location.pathname === "/") return <Spinner />;
    return <Navigate to="/login" />;
  }

  return <Outlet />;
}

function ProxyLogin(){
  const dispatch = useDispatch<any>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user, status } = useSelector(authSelector);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    if(token){
      sessionStorage.setItem("token", token);
      dispatch(setThirdPartyUser(false));
      dispatch(whoAmI()).unwrap().catch((err: any) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
        }
      });
      
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [token, dispatch]);
  
  if (user && user.is_deleted) {
    dispatch(logout());
    return <Unauthorized />;
  } else if (user) {
    return <Navigate to="/home" />;
  } else if (status === "failed") {
    return <Navigate to="/login" />;
  } else {
    return <Spinner />;
  }
}